import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { 
  Crown, 
  TrendingUp, 
  TrendingDown,
  PieChart as PieChartIcon,
  BarChart3,
  Activity,
  Target,
  Shield,
  Lock,
  Loader2,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Color palette for charts
const COLORS = ['#10b981', '#6366f1', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

const ProAnalyticsPage = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [scoreHistory, setScoreHistory] = useState([]);

  const isPro = user?.is_pro;

  useEffect(() => {
    if (token && isPro) {
      fetchAnalytics();
    } else {
      setLoading(false);
    }
  }, [token, isPro]);

  const fetchAnalytics = async () => {
    try {
      // Fetch portfolio data
      const portfolioRes = await fetch(`${API_URL}/api/portfolio/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const portfolioData = await portfolioRes.json();
      setPortfolio(portfolioData);

      // Fetch analytics
      const analyticsRes = await fetch(`${API_URL}/api/analytics/portfolio`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (analyticsRes.ok) {
        const analyticsData = await analyticsRes.json();
        setAnalytics(analyticsData);
      }

      // Fetch score history
      const historyRes = await fetch(`${API_URL}/api/analytics/score-history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (historyRes.ok) {
        const historyData = await historyRes.json();
        setScoreHistory(historyData.history || []);
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  // Pro gate - show upgrade prompt for non-Pro users
  if (!isPro) {
    return (
      <div className="min-h-screen bg-slate-950 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="py-16 text-center">
              <div className="inline-flex items-center justify-center p-4 bg-yellow-500/10 rounded-full mb-6">
                <Lock className="h-12 w-12 text-yellow-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Pro Analytics</h2>
              <p className="text-slate-400 mb-8 max-w-md mx-auto">
                Unlock advanced portfolio analytics, performance charts, and detailed insights with Trader Titans Pro.
              </p>
              <Link to="/subscription">
                <Button className="bg-yellow-500 hover:bg-yellow-400 text-black font-semibold">
                  <Crown className="h-4 w-4 mr-2" />
                  Upgrade to Pro
                </Button>
              </Link>
              
              <div className="mt-12 grid sm:grid-cols-3 gap-6 text-left">
                <FeaturePreview 
                  icon={<PieChartIcon className="h-5 w-5" />}
                  title="Asset Allocation"
                  description="Visual breakdown of your portfolio by asset type"
                />
                <FeaturePreview 
                  icon={<Activity className="h-5 w-5" />}
                  title="Performance Charts"
                  description="Track your points over time with interactive charts"
                />
                <FeaturePreview 
                  icon={<Target className="h-5 w-5" />}
                  title="Risk Analysis"
                  description="Understand your portfolio's risk exposure"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
      </div>
    );
  }

  // Prepare chart data
  const allocationData = analytics?.allocation || [];
  const performanceData = analytics?.performance || [];
  const riskMetrics = analytics?.risk_metrics || {};

  return (
    <div className="min-h-screen bg-slate-950 py-8 px-4" data-testid="pro-analytics-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
              <Crown className="h-8 w-8 text-yellow-400" />
              Pro Analytics
            </h1>
            <p className="text-slate-400 mt-1">Advanced insights for your portfolio</p>
          </div>
          <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
            Pro Member
          </Badge>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <MetricCard 
            label="Total Value"
            value={`V$${(analytics?.total_value || 0).toLocaleString()}`}
            change={analytics?.value_change}
            icon={<TrendingUp className="h-5 w-5" />}
          />
          <MetricCard 
            label="Total Points"
            value={analytics?.total_points || 0}
            change={analytics?.points_change}
            icon={<Target className="h-5 w-5" />}
          />
          <MetricCard 
            label="Win Rate"
            value={`${analytics?.win_rate || 0}%`}
            icon={<Activity className="h-5 w-5" />}
          />
          <MetricCard 
            label="Best Performer"
            value={analytics?.best_performer || 'N/A'}
            subtext={analytics?.best_performer_gain ? `+${analytics.best_performer_gain}%` : ''}
            icon={<ArrowUpRight className="h-5 w-5" />}
          />
        </div>

        {/* Charts Tabs */}
        <Tabs defaultValue="allocation" className="space-y-6">
          <TabsList className="bg-slate-800 border-slate-700">
            <TabsTrigger value="allocation">Allocation</TabsTrigger>
            <TabsTrigger value="performance">Performance</TabsTrigger>
            <TabsTrigger value="history">Score History</TabsTrigger>
            <TabsTrigger value="holdings">Holdings</TabsTrigger>
          </TabsList>

          {/* Allocation Tab */}
          <TabsContent value="allocation">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="bg-slate-900/50 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white">Asset Allocation</CardTitle>
                  <CardDescription>Portfolio breakdown by asset type</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={allocationData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={5}
                          dataKey="value"
                          nameKey="name"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {allocationData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
                          formatter={(value) => [`V$${value.toLocaleString()}`, 'Value']}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900/50 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white">Risk Distribution</CardTitle>
                  <CardDescription>Volatility exposure by asset</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={allocationData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis type="number" stroke="#94a3b8" />
                        <YAxis dataKey="name" type="category" stroke="#94a3b8" width={80} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
                        />
                        <Bar dataKey="value" fill="#10b981" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance">
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Portfolio Performance</CardTitle>
                <CardDescription>Value changes over time</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={performanceData}>
                      <defs>
                        <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="date" stroke="#94a3b8" />
                      <YAxis stroke="#94a3b8" />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
                        formatter={(value) => [`V$${value.toLocaleString()}`, 'Value']}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="value" 
                        stroke="#10b981" 
                        fillOpacity={1} 
                        fill="url(#colorValue)" 
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Score History Tab */}
          <TabsContent value="history">
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Weekly Score History</CardTitle>
                <CardDescription>Points earned each week</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={scoreHistory}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="week" stroke="#94a3b8" />
                      <YAxis stroke="#94a3b8" />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="points" 
                        stroke="#6366f1" 
                        strokeWidth={2}
                        dot={{ fill: '#6366f1', strokeWidth: 2 }}
                        name="Points"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="rank" 
                        stroke="#f59e0b" 
                        strokeWidth={2}
                        dot={{ fill: '#f59e0b', strokeWidth: 2 }}
                        name="Rank"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Holdings Tab */}
          <TabsContent value="holdings">
            <Card className="bg-slate-900/50 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white">Holdings Analysis</CardTitle>
                <CardDescription>Detailed breakdown of each position</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Symbol</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Type</th>
                        <th className="text-right py-3 px-4 text-slate-400 font-medium">Quantity</th>
                        <th className="text-right py-3 px-4 text-slate-400 font-medium">Avg Cost</th>
                        <th className="text-right py-3 px-4 text-slate-400 font-medium">Current</th>
                        <th className="text-right py-3 px-4 text-slate-400 font-medium">P/L</th>
                        <th className="text-right py-3 px-4 text-slate-400 font-medium">% Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {portfolio?.holdings?.map((holding, idx) => {
                        // Handle different field names from different data sources
                        const quantity = holding.quantity || holding.units || 0;
                        const buyPrice = holding.buy_price || holding.buy_price_vd || 0;
                        const currentPrice = holding.current_price || holding.current_price_vd || buyPrice;
                        const holdingType = holding.type || 'N/A';
                        
                        const pl = (currentPrice - buyPrice) * quantity;
                        const plPercent = buyPrice > 0 ? ((currentPrice - buyPrice) / buyPrice) * 100 : 0;
                        const isPositive = pl >= 0;
                        
                        return (
                          <tr key={idx} className="border-b border-slate-800 hover:bg-slate-800/50">
                            <td className="py-3 px-4">
                              <span className="font-medium text-white">{holding.symbol}</span>
                              {portfolio.power_play_symbol === holding.symbol && (
                                <Badge className="ml-2 bg-purple-500/20 text-purple-400 text-xs">3x</Badge>
                              )}
                            </td>
                            <td className="py-3 px-4 text-slate-400 capitalize">{holdingType}</td>
                            <td className="py-3 px-4 text-right text-slate-300">{quantity?.toFixed(4)}</td>
                            <td className="py-3 px-4 text-right text-slate-300">V${buyPrice?.toFixed(2)}</td>
                            <td className="py-3 px-4 text-right text-slate-300">V${currentPrice?.toFixed(2)}</td>
                            <td className={`py-3 px-4 text-right font-medium ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                              {isPositive ? '+' : ''}V${pl.toFixed(2)}
                            </td>
                            <td className={`py-3 px-4 text-right ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                              <span className="flex items-center justify-end gap-1">
                                {isPositive ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
                                {Math.abs(plPercent).toFixed(2)}%
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Helper Components
const MetricCard = ({ label, value, change, subtext, icon }) => {
  const isPositive = change > 0;
  
  return (
    <Card className="bg-slate-900/50 border-slate-800">
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-slate-400 text-sm">{label}</span>
          <span className="text-slate-500">{icon}</span>
        </div>
        <div className="text-2xl font-bold text-white">{value}</div>
        {change !== undefined && (
          <div className={`text-sm mt-1 ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
            {isPositive ? '+' : ''}{change}%
          </div>
        )}
        {subtext && <div className="text-sm text-emerald-400 mt-1">{subtext}</div>}
      </CardContent>
    </Card>
  );
};

const FeaturePreview = ({ icon, title, description }) => (
  <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
    <div className="text-yellow-400 mb-2">{icon}</div>
    <h3 className="font-medium text-white mb-1">{title}</h3>
    <p className="text-sm text-slate-400">{description}</p>
  </div>
);

export default ProAnalyticsPage;
